import * as React from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import YouTube from 'react-youtube';

import * as util from '../../utils/util';
import { CarePlanExercise, useCarePlan } from '../../utils/carePlan';
import plan from '../../assets/main/plan.png';

/**
 * 홈 화면(MainCare)의 '맞춤플랜' 섹션.
 *
 * 홈과 디지털트윈 화면 2가 같은 것을 보여줘야 해서 컴포넌트로 분리했다 —
 * 양쪽에 markup 을 복사해 두면 한쪽만 고쳐져 서로 달라진다.
 *
 * 표시 분기 (홈에 있던 것과 동일):
 *   미로그인      → 로그인 안내 + '로그인 하기'
 *   플랜 없음      → 맞춤프로그램 안내 + '맞춤플랜 시작' (CareStep)
 *   플랜 있음      → 이번주 맞춤식단(레시피 썸네일) + 이번주 맞춤운동(YouTube)
 */

// AP_004_002 의 recipe_recommend 초기값 — 필드가 없으면 화면이 undefined 를 렌더한다
const recipeObj = {
  category: '',
  cholesterol: '',
  content: '',
  description: '',
  disease: '',
  efficacy: '',
  id: '',
  kcal: '',
  materials: '',
  name: '',
  process: '',
  salt: '',
  sfa: '',
  sugar: '',
  thumbnail: '',
  type: '',
  update_date: '',
  url: '',
};

/** 유튜브 URL 끝의 videoId (홈 화면이 쓰던 방식과 동일) */
const youtubeId = (url?: string) => (url ? url.split('/').reverse()[0] : '');

/**
 * 디지털트윈 화면 2 의 건강뉴스 카드와 같은 카드 모양.
 *
 * display 를 block 이 아니라 flex(column) 로 두는 이유:
 * ButtonBase 는 <button> 이고, 브라우저는 버튼 내용이 버튼보다 짧으면 내용을
 * 세로 가운데로 밀어 놓는다. 두 카드는 그리드에서 같은 높이로 늘어나므로
 * 제목이 짧은 쪽만 내용이 내려앉아 위에 여백이 생기고 썸네일 위치가 어긋난다.
 * flex 로 두면 그 기본 정렬이 적용되지 않아 양쪽이 같은 위치에서 시작한다.
 */
const compactCardSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  width: '100%',
  textAlign: 'left',
  borderRadius: '12px',
  overflow: 'hidden',
  bgcolor: '#fff',
  boxShadow: '0 2px 6px rgba(197,211,218,.45)',
};

/**
 * 건강뉴스 카드 제목과 같은 2줄 클램프.
 * minHeight 를 2줄(2 × lineHeight 1.3 = 2.6em)로 고정해 제목이 1줄인 카드와
 * 2줄인 카드의 글자 영역 높이가 같아지게 한다.
 */
const compactTitleSx = {
  color: '#333',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.3,
  minHeight: '2.6em',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '2',
  overflow: 'hidden',
};

/** 건강뉴스 카드와 같은 썸네일 높이 */
const COMPACT_MEDIA_HEIGHT = 90;

type MainCarePlanProps = {
  /**
   * 'full'    — 홈 화면. 레시피 180px + 유튜브 플레이어 200px 를 세로로 쌓는다.
   * 'compact' — 디지털트윈 화면 2. 옆 건강뉴스와 같은 2열 90px 카드로 줄인다.
   *             데이터·분기·이동 경로는 'full' 과 완전히 같고 크기만 다르다.
   */
  variant?: 'full' | 'compact';
};

const MainCarePlan = ({ variant = 'full' }: MainCarePlanProps) => {
  const navigate = useNavigate();
  const isCompact = variant === 'compact';

  // AP_004_002 는 MainCareSports 와 모듈 캐시를 공유한다 (한 화면에 둘 다 있어도 요청 1회)
  const { data: carePlan, loading } = useCarePlan();

  const isLogin = util.strNull(window.localStorage.userCusID) !== '';
  const existPlan = carePlan?.exists ?? false;
  const recipeRecommend = carePlan?.data?.recipe_recommend ?? recipeObj;

  // 근력운동 + 스트레칭을 한 목록으로 (홈에 있던 weight.concat(stretching) 과 같다)
  const customerProgram = useMemo<CarePlanExercise[]>(() => {
    const list = [...(carePlan?.data?.weight ?? []), ...(carePlan?.data?.stretching ?? [])];
    return list.length > 0 ? list : [{ url: '', name: '' }];
  }, [carePlan]);

  // 이번주 맞춤운동으로 보여줄 한 건을 무작위로 고른다 (홈과 같은 동작)
  const program = useMemo(
    () => customerProgram[Math.floor(Math.random() * customerProgram.length)] ?? customerProgram[0],
    [customerProgram],
  );

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {/* 제목 줄 — compact 는 옆 건강뉴스 제목/전체보기와 같은 크기로 맞춘다.
          '전체보기'는 플랜이 있을 때만 — 없는 상태로 CareMenu 에 들어가면
          그 화면이 빈 careStore 값으로 플랜을 만들어 버린다. */}
      {isCompact ? (
        <Stack direction="row" alignItems="center" sx={{ mb: 1.2 }}>
          <Typography sx={{ color: '#333', fontSize: 15, fontWeight: 800 }}>맞춤플랜</Typography>
          {existPlan && (
            <ButtonBase
              onClick={() => navigate('/care/CareMenu')}
              sx={{ ml: 'auto', color: '#888', fontSize: 13, fontWeight: 600, borderRadius: '8px', px: 0.5 }}
            >
              전체보기 ›
            </ButtonBase>
          )}
        </Stack>
      ) : (
        <Stack direction={'row'} alignItems={'center'}>
          <Typography sx={{ fontWeight: 700, color: '#777', fontSize: 20 }}>맞춤플랜</Typography>
          {existPlan ? (
            <Typography sx={{ ml: 'auto', fontSize: 14, }} onClick={() => navigate('/care/CareMenu')}>전체보기 <ChevronRightIcon fontSize='small' sx={{ verticalAlign: 'sub', color: '#555' }} /></Typography>
          ) : (<React.Fragment></React.Fragment>)}
        </Stack>
      )}

      {!isLogin ? (

        <Box sx={{ bgcolor: '#fff', mt: isCompact ? 0 : 2, borderRadius: '20px', p: 2 }}>

          <Typography sx={{ fontSize: 14, color: '#666', mr: 2 }}>로그인을 하면 맞춤프로그램을 통해 개인의 건강상태에 맞춘
            권장 섭취 칼로리, 레시피, 운동프로그램을 제공해 드립니다.
          </Typography>

          <Stack direction={'row'}>
            <Button onClick={() => navigate('/login')} sx={{ textAlign: 'center', width: '100%', fontWeight: 700, pb: 0, pt: 2, color: '#EF8322' }}>
              로그인 하기
            </Button>
            <ListItemAvatar sx={{ bgcolor: '#FEF8F5', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 'auto', mt: 1 }}>
              <Avatar alt="" src={plan} sx={{ borderRadius: 0 }} />
            </ListItemAvatar>
          </Stack>

        </Box>

      ) : (

        !existPlan ? (
          <Box sx={{ bgcolor: '#fff', mt: isCompact ? 0 : 2, borderRadius: '20px', p: 2 }}>

            <Typography sx={{ fontSize: 14, color: '#666' }}>맞춤프로그램을 통해 개인의 건강상태에 맞춘
              권장 섭취 칼로리, 레시피, 운동프로그램을 제공해 드립니다.
            </Typography>

            <Stack direction={'row'}>
              <Button onClick={() => navigate('/care/CareStep')} sx={{ textAlign: 'center', width: '100%', fontWeight: 700, pb: 0, pt: 2, color: '#EF8322' }}>
                맞춤플랜 시작
              </Button>
              <ListItemAvatar sx={{ bgcolor: '#FEF8F5', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 'auto', mt: 1 }}>
                <Avatar alt="" src={plan} sx={{ borderRadius: 0 }} />
              </ListItemAvatar>
            </Stack>

          </Box>
        ) : isCompact ? (

          /*
           * compact — 옆 건강뉴스와 같은 2열 90px 카드.
           * 유튜브는 플레이어 대신 영상 썸네일 + 재생 표시를 쓴다. 이 크기(약 195×90)
           * 에서는 플레이어 UI가 화면을 다 덮어 재생이 사실상 불가능하고, 카드 모양도
           * 옆 건강뉴스와 어긋난다. 눌러서 맞춤운동 화면에 들어가면 원래 크기로 재생된다.
           */
          loading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 }}>
              {[0, 1].map((slot) => (
                <Box key={slot} sx={{ borderRadius: '12px', overflow: 'hidden', bgcolor: '#fff' }}>
                  <Skeleton variant="rectangular" height={COMPACT_MEDIA_HEIGHT} />
                  <Box sx={{ p: 1 }}>
                    <Skeleton height={16} />
                    <Skeleton height={14} width="60%" />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 }}>
              {/* 이번주 맞춤식단 */}
              <ButtonBase onClick={() => navigate('/care/CareMenu')} sx={compactCardSx}>
                <Box
                  sx={{ height: COMPACT_MEDIA_HEIGHT, flexShrink: 0, bgcolor: '#eef2f6', overflow: 'hidden' }}
                >
                  {/* 초기값이 '' 이라 방어 없이 넘기면 src="" 가 되어 현재 페이지를 이미지로 요청한다 */}
                  <Box
                    component="img"
                    src={recipeRecommend.thumbnail || undefined}
                    alt={recipeRecommend.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography sx={compactTitleSx}>이번주 맞춤식단 [{recipeRecommend.name}]</Typography>
                </Box>
              </ButtonBase>

              {/* 이번주 맞춤운동 */}
              <ButtonBase onClick={() => navigate('/care/CareSports')} sx={compactCardSx}>
                <Box
                  sx={{
                    height: COMPACT_MEDIA_HEIGHT,
                    flexShrink: 0,
                    bgcolor: '#eef2f6',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {youtubeId(program.url) ? (
                    <>
                      <Box
                        component="img"
                        src={`https://img.youtube.com/vi/${youtubeId(program.url)}/mqdefault.jpg`}
                        alt={program.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,.55)',
                            color: '#fff',
                            fontSize: 12,
                            pl: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          ▶
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1,
                        color: '#9aa6b2',
                        fontSize: 11,
                        textAlign: 'center',
                      }}
                    >
                      관련 영상이 존재하지 않습니다.
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 1 }}>
                  <Typography sx={compactTitleSx}>이번주 맞춤운동 [{program.name}]</Typography>
                </Box>
              </ButtonBase>
            </Box>
          )

        ) : (

          <Box sx={{}}>
            <ImageList sx={{ display: 'block', pb: 1 }}>
              {loading ? (
                <Stack direction={'column'} sx={{ display: loading ? 'flex' : 'none', width: '100%' }} justifyContent='center' alignContent={'center'}>
                  <Skeleton animation="wave" variant="rounded" height={180} width='100%' sx={{ borderRadius: 3, }} />
                  <Skeleton animation="wave" variant="rounded" height={21} width='80%' sx={{ borderRadius: 3, my: 1, mx: 'auto' }} />
                </Stack>
              ) : (
                <ImageListItem onClick={() => navigate('/care/CareMenu')} key={1} sx={{

                  '& .MuiImageListItem-img': { height: 180, borderRadius: 4 }
                }}>
                  {/* recipeObj 초기값이 전부 '' 이라 방어 없이 넘기면 src="" 가 되어
                      브라우저가 현재 페이지를 이미지로 로드하려 한다(IMG localhost:3000/). */}
                  <img
                    src={recipeRecommend.thumbnail || undefined}
                    alt={recipeRecommend.name}
                  />
                  <Typography
                    sx={{
                      textAlign: 'center',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      mt: 1, mb: 1,
                      fontSize: 14,
                      fontWeight: 700
                    }}>
                    이번주 맞춤식단 [{recipeRecommend.name}]
                  </Typography>
                </ImageListItem>
              )}
            </ImageList>

            <Paper
              elevation={0}
              sx={{
                display: 'block',
                alignItems: 'center',
                height: 200,
                // 위 레시피 썸네일(.MuiImageListItem-img 의 borderRadius 4 = 16px)과 같은 라운드.
                // iframe 은 부모의 overflow:hidden 으로 안 깎이는 브라우저가 있어
                // iframe 자체에도 같은 값을 준다.
                borderRadius: 4,
                overflow: 'hidden',
                '& iframe': { display: 'block', borderRadius: 4 },
              }}
            >
              {loading ? (
                <Stack direction={'column'} sx={{ display: loading ? 'flex' : 'none', width: '100%' }} justifyContent='center' alignContent={'center'}>
                  <Skeleton animation="wave" variant="rounded" height={200} width='100%' sx={{ borderRadius: 3, }} />
                </Stack>
              ) : (
                <div key={program.name}>
                  {/*  Youtube Component 사용 라이브러리 :  https://github.com/tjallingt/react-youtube   */}
                  {program.url === undefined || program.url === '' ? (
                    <Typography>관련 영상이 존재하지 않습니다.</Typography>
                  ) : (
                    <YouTube
                      opts={{
                        height: 200,
                        display: 'block',
                        overflow: 'hidden',
                        width: '100%',
                      }}
                      videoId={program.url.split('/').reverse()[0]}
                    />
                  )}
                </div>
              )}
            </Paper>

            {loading ? (
              <Skeleton animation="wave" variant="rounded" height={21} width='80%' sx={{ borderRadius: 3, my: 1, mx: 'auto' }} />
            ) : (
              <Typography sx={{ textAlign: 'center', textOverflow: 'ellipsis', overflow: 'hidden', mt: 1, mb: 1, fontSize: 14, fontWeight: 700 }}>
                이번주 맞춤운동 [{program.name}]
              </Typography>
            )}
          </Box>

        )

      )}
    </Box>
  );
}

export default MainCarePlan;
